import { useEffect, useRef, useCallback } from 'react';
import { useStore } from '@/store/useStore';
import { useAudio } from '@/contexts/AudioContext';
import { debugLog } from '@/utils/debugLogger';

/**
 * 🎯 SOLUZIONE DEFINITIVA: Polling intelligente che rispetta TTS
 * 
 * 🛡️ IMPORTANTE: Questo hook viene montato SOLO in ClientOnlyAuthSideEffects,
 * che a sua volta viene renderizzato SOLO dopo isSafeToMountSideEffects === true.
 * Quindi NON abbiamo bisogno di una hydration guard interna - siamo GIÀ post-hydration.
 * 
 * - PAUSA durante audio TTS attivo
 * - RIPRENDE immediatamente dopo TTS
 * - BATCH UPDATES per eliminare re-render multipli
 */
export function useSimplifiedPricePolling() {
  const { fetchCurrentPricesData } = useStore();
  const { isAudioProtectionMode } = useAudio();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingFetchRef = useRef<boolean>(false);
  
  
  // Memoize audio check to avoid calling function in dependency arrays
  const checkAudioProtection = useCallback(() => {
    return isAudioProtectionMode();
  }, [isAudioProtectionMode]);
  
  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // Already polling
    
    intervalRef.current = setInterval(() => {
      // 🔇 PAUSA durante TTS attivo
      if (checkAudioProtection()) {
        pendingFetchRef.current = true; // Mark as needing update
        debugLog('pricing', '⏸️ Price update PAUSED (TTS active)', undefined, 'useSimplifiedPricePolling');
        return;
      }
      
      // ✅ Fetch prezzi normalmente
      debugLog('pricing', '🔄 Market price update', undefined, 'useSimplifiedPricePolling');
      fetchCurrentPricesData();
      pendingFetchRef.current = false;
    }, 30000); // 30 seconds interval
    
    debugLog('pricing', 'Price polling STARTED (30s interval)', undefined, 'useSimplifiedPricePolling');
  }, [checkAudioProtection, fetchCurrentPricesData]);
  
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      debugLog('pricing', 'Price polling STOPPED', undefined, 'useSimplifiedPricePolling');
    }
  }, []);
  
  // 🎯 MAIN EFFECT: Siamo GIÀ post-hydration (montato in ClientOnlyAuthSideEffects)
  // Nessuna hydration guard interna necessaria
  useEffect(() => {
    debugLog('pricing', '🔄 Starting price fetch and polling (post-hydration mount)', undefined, 'useSimplifiedPricePolling');
    fetchCurrentPricesData();
    startPolling();
    
    return () => {
      stopPolling();
    };
  }, [fetchCurrentPricesData, startPolling, stopPolling]);
  
  // 🎯 RIPRENDI dopo TTS
  useEffect(() => {
    // Check periodically if audio protection ended and we have pending fetch
    const checkInterval = setInterval(() => {
      if (!checkAudioProtection() && pendingFetchRef.current) {
        debugLog('pricing', '▶️ Price update RESUMED (TTS ended, executing pending)', undefined, 'useSimplifiedPricePolling');
        fetchCurrentPricesData();
        pendingFetchRef.current = false;
      }
    }, 1000);
    
    return () => clearInterval(checkInterval);
  }, [checkAudioProtection, fetchCurrentPricesData]);
}
